import React, { useEffect, useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { MessageSquare, AlertTriangle } from 'lucide-react'
import { DialogTitle, DialogContent, DialogActions, Button, Switch } from '@diversioteam/diversio-ds'
import { toast } from 'sonner'

import { IFeedbackModalProps } from './FeedbackModal.types'
import { FEEDBACK_TYPES } from './FeedbackModal.data'
import { feedbackFormResolver, VALIDATION_CONSTANTS } from './FeedbackModal.validation'
import * as S from './FeedbackModal.styles'

import { useSubmitFeedback, type FeedbackFormData } from '@/hooks/feedback/useSubmitFeedback'
import { FormTextarea } from '@/components/forms/FormTextarea'

export const FeedbackModal = ({ isOpen, onClose }: IFeedbackModalProps) => {
    const [apiError, setApiError] = useState<string | null>(null)

    const methods = useForm<FeedbackFormData>({
        resolver: feedbackFormResolver,
        defaultValues: {
            request_type: '' as FeedbackFormData['request_type'],
            description: '', // Changed from feedback_text to match backend API
            wants_follow_up: false,
        },
        mode: 'onChange',
    })

    const {
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = methods
    const submitFeedbackMutation = useSubmitFeedback()

    // Watch form values for validation feedback
    const watchedValues = watch()

    // Handle form submission
    const onSubmit = async (data: FeedbackFormData) => {
        setApiError(null)

        try {
            await submitFeedbackMutation.mutateAsync(data)
            // Success: close modal and show toast
            handleModalClose()
            toast.success('Feedback submitted successfully!')
        } catch (error) {
            // Handle error inline
            const axiosError = error as { response?: { status?: number; data?: { detail?: string } } }
            const status = axiosError?.response?.status
            const detail = axiosError?.response?.data?.detail

            if (status === 429) {
                setApiError('Too many submissions. Try again later.')
            } else if (status === 400 && detail?.includes('Validation')) {
                setApiError('Please check your feedback and try again.')
            } else if (status === 401) {
                setApiError('Please sign in to submit feedback.')
            } else {
                setApiError(detail || 'Failed to submit feedback. Please try again.')
            }
        }
    }

    // Handle modal close and cleanup
    const handleModalClose = useCallback(() => {
        reset() // Reset form state
        setApiError(null) // Clear any errors
        onClose()
    }, [reset, onClose])

    // Clear error when modal closes
    useEffect(() => {
        if (!isOpen) {
            setApiError(null)
        }
    }, [isOpen])

    return (
        <S.Dialog open={isOpen} onClose={handleModalClose} hideBackdrop={false}>
            <DialogTitle>
                <S.DialogTitleContent>
                    <S.TitleIcon>
                        <MessageSquare style={{ height: '100%', width: '100%' }} />
                    </S.TitleIcon>
                    Share Your Feedback
                </S.DialogTitleContent>
            </DialogTitle>
            <DialogContent>
                {/* Loading overlay for form content only */}
                {submitFeedbackMutation.isPending && <S.LoadingOverlay />}

                <S.Description>
                    Help us improve Optimo by sharing your thoughts, reporting issues, or suggesting new features.
                </S.Description>

                <FormProvider {...methods}>
                    <S.Form onSubmit={handleSubmit(onSubmit)}>
                        {/* Feedback Type Selection */}
                        <S.FieldContainer>
                            <S.FieldLabel>Feedback Type *</S.FieldLabel>
                            <S.TypeGrid>
                                {FEEDBACK_TYPES.map((type) => {
                                    const IconComponent = type.icon
                                    const isSelected = watchedValues.request_type === type.value
                                    return (
                                        <S.TypeButton
                                            key={type.value}
                                            type="button"
                                            onClick={() =>
                                                setValue(
                                                    'request_type',
                                                    type.value as FeedbackFormData['request_type'],
                                                    {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    },
                                                )
                                            }
                                            $isSelected={isSelected}
                                            disabled={submitFeedbackMutation.isPending}
                                        >
                                            <S.TypeButtonContent>
                                                <S.TypeIcon>
                                                    <IconComponent
                                                        style={{
                                                            height: '100%',
                                                            width: '100%',
                                                        }}
                                                    />
                                                </S.TypeIcon>
                                                <S.TypeLabel>{type.label}</S.TypeLabel>
                                            </S.TypeButtonContent>
                                        </S.TypeButton>
                                    )
                                })}
                            </S.TypeGrid>
                            {errors.request_type && <S.ErrorText>{errors.request_type.message}</S.ErrorText>}
                        </S.FieldContainer>

                        {/* Feedback Text with Character Counter */}
                        <S.FieldContainer>
                            <S.FieldLabel>Tell us what happened or what could be better *</S.FieldLabel>
                            <FormTextarea
                                name="description"
                                placeholder="Tell us what happened or what could be better…"
                                minRows={4}
                                maxRows={8}
                                maxHeight={200}
                                maxLength={VALIDATION_CONSTANTS.MAX_CHARACTERS}
                                showCharacterCount
                                disabled={submitFeedbackMutation.isPending}
                                rules={{
                                    required: 'Please provide your feedback',
                                    minLength: {
                                        value: VALIDATION_CONSTANTS.MIN_CHARACTERS,
                                        message: `Feedback must be at least ${VALIDATION_CONSTANTS.MIN_CHARACTERS} characters`,
                                    },
                                    maxLength: {
                                        value: VALIDATION_CONSTANTS.MAX_CHARACTERS,
                                        message: `Feedback must be no more than ${VALIDATION_CONSTANTS.MAX_CHARACTERS} characters`,
                                    },
                                }}
                            />
                        </S.FieldContainer>

                        {/* Follow-up Toggle */}
                        <S.FieldContainer>
                            <S.SwitchContainer>
                                <S.SwitchLabel>
                                    Follow up with me
                                    <S.SwitchSubtext>
                                        We&apos;ll use your account email to follow up on your feedback
                                    </S.SwitchSubtext>
                                </S.SwitchLabel>
                                <Switch
                                    checked={watchedValues.wants_follow_up}
                                    onChange={(e) =>
                                        setValue('wants_follow_up', (e.target as HTMLInputElement).checked, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        })
                                    }
                                    disabled={submitFeedbackMutation.isPending}
                                />
                            </S.SwitchContainer>
                        </S.FieldContainer>
                    </S.Form>
                </FormProvider>

                {/* Error display */}
                {apiError && (
                    <S.ErrorContainer>
                        <S.ErrorIcon>
                            <AlertTriangle style={{ height: '100%', width: '100%' }} />
                        </S.ErrorIcon>
                        <S.ErrorMessage>Error: {apiError}</S.ErrorMessage>
                    </S.ErrorContainer>
                )}
            </DialogContent>
            <DialogActions>
                <S.ButtonContainer>
                    <Button
                        type="button"
                        color="secondaryLight"
                        onClick={handleModalClose}
                        fullWidth
                        disabled={submitFeedbackMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        disabled={submitFeedbackMutation.isPending}
                        fullWidth
                        onClick={handleSubmit(onSubmit)}
                        loading={submitFeedbackMutation.isPending}
                    >
                        {submitFeedbackMutation.isPending ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                </S.ButtonContainer>
            </DialogActions>
        </S.Dialog>
    )
}
